import {OperationCommercialeCoverItem} from '@/domains/operation_commerciale/components/OperationCommercialeCoverItem.tsx';
import {Flex, Grid} from '@chakra-ui/react';
import {OperationCommerciale} from 'api-types/ffconnect';
import {OperationsCommercialesListeViewContext} from '@/domains/operation_commerciale/OperationCommercialeListeView.tsx';

export type OperationCommercialeListeDirection =
  | 'row'
  | 'column'
  | 'grille'
  | 'deux_colonnes';

interface OperationsCommercialesListeProps {
  operations_commerciales: OperationCommerciale[];
  direction?: OperationCommercialeListeDirection;
  context?: OperationsCommercialesListeViewContext;
  onRedirect?: () => void;
}

// Écart entre deux bandeaux de la grille, repris dans le calcul de la largeur des colonnes.
const ECART_GRILLE = '24px';
// Trois bandeaux de 532 px au plus, soit 164 px de haut au ratio du cadrage, et deux écarts.
const LARGEUR_MAX_GRILLE = '1644px';
// Une colonne de 808 px au plus, puis deux bandeaux de 808 px au plus et un écart : 249 px de
// haut au plus au ratio du cadrage, comme l'aperçu « Ordinateur » du backoffice.
const LARGEUR_MAX_UNE_COLONNE = '808px';
const LARGEUR_MAX_DEUX_COLONNES = {base: LARGEUR_MAX_UNE_COLONNE, xl: '1640px'};

/**
 * Grille/liste des cartes d'opérations commerciales.
 * En row : défilement horizontal (recherche). En column : empilées verticalement (menu catalogue, colonne de recherche).
 * En grille (accueil) : trois colonnes sur ordinateur, deux sur tablette, défilement horizontal sur téléphone.
 * En deux colonnes (onglet "En ce moment") : deux colonnes sur ordinateur, une seule en dessous.
 */
export const OperationsCommercialesListe = ({
  operations_commerciales,
  direction = 'row',
  context,
  onRedirect,
}: OperationsCommercialesListeProps) => {
  const bandeaux = operations_commerciales.map((oc) => (
    <OperationCommercialeCoverItem
      key={oc.id}
      id_operation_commerciale={oc.id}
      nom={oc.nom!}
      description={oc.description!}
      banniere_url={oc.banniere_url!}
      banniere_url_mobile={oc.banniere_url_mobile ?? undefined}
      banniere_couleur={oc.banniere_couleur ?? undefined}
      banniere_cadrage={oc.banniere_cadrage ?? undefined}
      context={context}
      onRedirect={onRedirect}
      date_fin={oc.date_fin ?? undefined}
      affiche_date_fin={oc.affiche_date_fin}
    />
  ));

  if (direction === 'grille') {
    // Chaque bandeau garde la largeur d'un tiers de rangée, donc la même hauteur au ratio dans
    // toutes les rangées ; un badge de date peut allonger la sienne, et les rangées en `1fr`
    // prennent alors toutes la hauteur de la plus haute.
    // À quatre, deux colonnes seulement : deux rangées de deux plutôt que trois et un.
    const colonnes = operations_commerciales.length === 4 ? 2 : 3;
    return (
      // Les bandeaux sont les enfants directs de la grille : chacun s'étire à la hauteur de sa
      // rangée, ce qui aligne leurs bas.
      <Grid
        autoFlow={{base: 'column', md: 'row'}}
        autoRows='1fr' // Chaque rangée prend la hauteur de son contenu le plus haut
        autoColumns={{base: '300px', md: 'auto'}}
        templateColumns={{
          md: 'repeat(2, minmax(0, 1fr))',
          lg: `repeat(${colonnes}, calc((100% - 2 * ${ECART_GRILLE}) / 3))`,
        }}
        gap={ECART_GRILLE}
        overflowX={{base: 'auto', md: 'visible'}}
        maxW={LARGEUR_MAX_GRILLE}
      >
        {bandeaux}
      </Grid>
    );
  }

  if (direction === 'deux_colonnes') {
    // Une seule opération : la colonne unique, centrée, comme sur un écran plus étroit, plutôt
    // qu'un bandeau calé à gauche à côté d'une colonne vide.
    const plusieurs = operations_commerciales.length > 1;
    return (
      // Comme à l'accueil, les bandeaux sont les enfants directs de la grille pour que leurs bas
      // s'alignent dans une rangée. Sur une colonne, chacun garde sa hauteur, comme avant.
      <Grid
        templateColumns={{
          base: 'minmax(0, 1fr)',
          xl: plusieurs ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
        }}
        autoRows={{base: 'auto', xl: '1fr'}} // Sur deux colonnes, toutes les rangées prennent la hauteur de la plus haute
        gap={ECART_GRILLE}
        maxW={plusieurs ? LARGEUR_MAX_DEUX_COLONNES : LARGEUR_MAX_UNE_COLONNE}
        mx='auto'
        mt='5'
        pb={20}
        pt='5'
      >
        {bandeaux}
      </Grid>
    );
  }

  return (
    <Flex
      direction={direction}
      justifyContent='flex-start'
      gap={context === 'search_sidebar' ? 3 : 6}
      overflow='scroll'
    >
      {bandeaux}
    </Flex>
  );
};
